import { optimizePath } from '../../helpers/optimize-path';
import { AdUnitsService } from '../ad-units-service';
import { wallpaperFormat } from '../../helpers/wallpaper-format';
import { getDuid } from '../../helpers/custom-events';

import { consentModel } from '@module/consent-manager';

import { AppConfig } from '@app/core-bundle/config/app-config';

import LaunchDarkly from '@module/launchdarkly';
import request from '@module/http/axios/request';
import Privacy from '@module/privacy';

import { FF_COOKIE_WALL } from '@app/constant/experiments';

export const CookiewallProvider = {
  get name() {
    return 'COOKIEWALL_PROVIDER';
  },

  isRotationEnabled() {
    return true;
  },

  // Using same intervals as the advertising bucket
  rotationInterval(config, { wallpaper }) {
    const nextPosition = wallpaper.position + 1;

    // The rotation interval can be different based on the wallpaper position.
    // The higher the position, the longer the rotation interval.
    const interval = AdUnitsService.calculateInterval(nextPosition);

    return interval;
  },

  // Test if users should see cookiewall wallpapers.
  // Should return `true` if they should see the cookiewall wallpapers.
  test() {
    const canShowConsentWall = LaunchDarkly.getVariant(FF_COOKIE_WALL);

    // If the user is not part of the rollout phase, they shouldn't
    // see this wallpaper unless they have opted out in the previous
    // versions of the cookiewall.
    //
    // This will get evaluated more often than not so it makes sense
    // to check this first.
    if (!canShowConsentWall) {
      // Attempt to honor user privacy settings from previous cookiewall rollout.
      if (Privacy.userHasClosedBanner() && !Privacy.userAllowsTracking()) {
        return true;
      }

      return false;
    }

    // Show when the user rejects all consent
    if (consentModel.hasRejectedAll()) {
      return true;
    }

    // Show even when the user partially rejects consent.
    // No mercy.
    if (consentModel.hasPartialConsent()) {
      return true;
    }

    // Final test if we are going to show this provider.
    return !consentModel.hasFullConsent();
  },

  async nextCreative(config, { wallpaper }) {
    const nextPosition = wallpaper.position + 1;
    const zoneId = AdUnitsService.calculateSlots(nextPosition)[0];
    const duid = this.domainUserId();

    const response = await request({
      path: config.editorialUrl,
      params: {
        duid,
        zoneId,
        cwa: 1,
        version: 1,
      },
    });

    return this.parseResponse(config, response.body);
  },

  domainUserId() {
    const cookieName = AppConfig.cookies.snowplow.name;
    return getDuid(cookieName);
  },

  parseResponse(config, response) {
    const { src } = response;

    return {
      src: optimizePath({ src: `${config.host}${src}` }),
      format: wallpaperFormat(src),
      meta: {
        href: response?.meta?.href || '',
        title: response?.meta?.title || '',
      },
    };
  },
};
